"use client";

import {
  forwardRef,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type SelectHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "../../lib/cn";
import Combobox, {
  optionsFromSelectChildren,
  splitPlaceholderOption,
} from "@dt/Combobox";
import {
  FieldShell,
  fieldDescribedBy,
  fieldStatusClass,
  resolveFieldStatus,
  type FieldAction,
  type FieldStatus,
} from "@dt/FieldShell";
import styles from "./FormFieldEditorial.module.css";

type InputType = "text" | "email" | "tel" | "textarea" | "select";

interface BaseProps {
  /**
   * Field label, rendered by the shared FieldShell Label (Combobox label for
   * select).
   */
  label: string;
  /** Error message */
  error?: string;
  /** Semantic state: colours the border and styles statusMessage. `error` overrides it. */
  status?: FieldStatus;
  /** Message under the field for `status`, above the helper line */
  statusMessage?: string;
  /** Icon buttons at the end of the label row: each runs an operation or opens a menu */
  actions?: FieldAction[];
  /** Whether the field is required */
  required?: boolean;
  /** Additional className */
  className?: string;
  /** Field ID. When omitted it is generated from the label: lowercased, accents folded (Sähköposti becomes sahkoposti), spaces hyphenated and letters of any script kept; a label with no letters or digits gets a unique generated id. Two fields with the same label still need an explicit id. */
  id?: string;
  /** Supporting text under the field, linked with aria-describedby (select passes it to Combobox). Use it, for example, to say why a read-only value is fixed. */
  helperText?: string;
}

interface TextInputProps extends BaseProps, Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "id"> {
  /** Control to render; defaults to text. select renders Combobox over a hidden native select element that carries the forwarded ref, name, form and disabled, so the value submits with its form, onChange receives a real change event from that select and onBlur fires when focus leaves the field; defaultValue is an uncontrolled initial value. aria-describedby is passed to the Combobox trigger; other select attributes are dropped. */
  type?: "text" | "email" | "tel";
  children?: never;
}

interface TextareaProps extends BaseProps, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> {
  /** Control to render; defaults to text. select renders Combobox over a hidden native select element that carries the forwarded ref, name, form and disabled, so the value submits with its form, onChange receives a real change event from that select and onBlur fires when focus leaves the field; defaultValue is an uncontrolled initial value. aria-describedby is passed to the Combobox trigger; other select attributes are dropped. */
  type: "textarea";
  /** Number of rows for textarea */
  rows?: number;
  children?: never;
}

interface SelectProps extends BaseProps, Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> {
  /** Control to render; defaults to text. select renders Combobox over a hidden native select element that carries the forwarded ref, name, form and disabled, so the value submits with its form, onChange receives a real change event from that select and onBlur fires when focus leaves the field; defaultValue is an uncontrolled initial value. aria-describedby is passed to the Combobox trigger; other select attributes are dropped. */
  type: "select";
  /** Option elements for type="select", parsed into Combobox options; an option with an empty value becomes the placeholder. */
  children: ReactNode;
}

export type FormFieldEditorialProps = TextInputProps | TextareaProps | SelectProps;

function generateId(label: string): string {
  return label
    .normalize("NFKD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]/gu, "");
}

function setForwardedRef<T>(
  ref: React.ForwardedRef<T>,
  value: T | null,
) {
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  if (ref) {
    ref.current = value;
  }
}

/**
 * FormFieldEditorial component.
 */
export const FormFieldEditorial = forwardRef<
  HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  FormFieldEditorialProps
>(function FormFieldEditorial(props, ref) {
  const {
    label,
    error,
    status,
    statusMessage,
    actions,
    required,
    className,
    id: providedId,
    type = "text",
    helperText,
    ...restProps
  } = props;
  const fallbackId = useId();
  const slug = generateId(label);
  const id = providedId || (/[\p{L}\p{N}]/u.test(slug) ? slug : `field-${fallbackId}`);
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const isSelect = type === "select";
  const selectProps = isSelect
    ? (restProps as Omit<SelectProps, keyof BaseProps | "type">)
    : undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(() =>
    selectProps?.defaultValue === undefined || selectProps.defaultValue === null
      ? ""
      : String(selectProps.defaultValue),
  );
  const field = resolveFieldStatus({ status, statusMessage, error });
  const hasError = field.status === "error";
  const describe = (external?: string) =>
    fieldDescribedBy(id, { statusMessage: field.statusMessage, helperText }, external);

  const shell = (control: ReactNode) => (
    <FieldShell
      controlId={id}
      label={label}
      required={required}
      disabled={"disabled" in restProps ? !!restProps.disabled : false}
      status={field.status}
      statusMessage={field.statusMessage}
      helperText={helperText}
      actions={actions}
      className={className}
    >
      {control}
    </FieldShell>
  );

  if (type === "textarea") {
    const { children, rows = 5, ...textareaProps } = restProps as Omit<TextareaProps, keyof BaseProps | "type">;
    const {
      "aria-describedby": externalDescribedBy,
      ...forwardedTextareaProps
    } = textareaProps;
    return shell(
      <textarea
        ref={(node) => setForwardedRef(ref, node)}
        id={id}
        rows={rows}
        className={cn(styles.textarea, fieldStatusClass(field.status))}
        aria-invalid={hasError}
        aria-required={required || undefined}
        aria-describedby={describe(externalDescribedBy)}
        {...forwardedTextareaProps}
      />,
    );
  }

  if (isSelect && selectProps) {
    const {
      children,
      value,
      onChange,
      onBlur,
      disabled,
      name,
      form,
      "aria-describedby": externalDescribedBy,
    } = selectProps;

    const parsedOptions = optionsFromSelectChildren(children);
    const { placeholder, options } = splitPlaceholderOption(parsedOptions);
    const isControlled = value !== undefined && value !== null;
    const currentValue = isControlled ? String(value) : uncontrolledValue;

    // A hidden native select mirrors the Combobox value. It carries the
    // forwarded ref, name and form, so the value submits with its form and
    // onChange receives a real change event whose target is this select.
    const attachSelect = (node: HTMLSelectElement | null) => {
      selectRef.current = node;
      if (node) {
        node.focus = () => triggerRef.current?.focus();
      }
      setForwardedRef(ref, node);
    };

    return (
      <div
        className={styles.selectRoot}
        onBlur={(event) => {
          if (!onBlur) return;
          const next = event.relatedTarget as Node | null;
          if (next && event.currentTarget.contains(next)) return;
          onBlur(event as unknown as React.FocusEvent<HTMLSelectElement>);
        }}
      >
        <Combobox
          ref={triggerRef}
          id={id}
          label={label}
          options={options}
          value={currentValue}
          placeholder={placeholder}
          onValueChange={(nextValue) => {
            const node = selectRef.current;
            if (!node) return;
            node.value = nextValue;
            node.dispatchEvent(new Event("change", { bubbles: true }));
          }}
          aria-describedby={externalDescribedBy}
          error={error}
          status={status}
          statusMessage={statusMessage}
          helperText={helperText}
          actions={actions}
          required={required}
          disabled={disabled}
          className={className}
        />
        <select
          ref={attachSelect}
          hidden
          aria-hidden="true"
          tabIndex={-1}
          name={name}
          form={form}
          disabled={disabled}
          value={currentValue}
          onChange={(event) => {
            if (!isControlled) setUncontrolledValue(event.target.value);
            onChange?.(event);
          }}
        >
          {parsedOptions.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Default: text, email, tel inputs
  const inputProps = restProps as Omit<TextInputProps, keyof BaseProps | "type">;
  const {
    "aria-describedby": externalDescribedBy,
    ...forwardedInputProps
  } = inputProps;
  return shell(
    <input
      ref={(node) => setForwardedRef(ref, node)}
      id={id}
      type={type}
      className={cn(styles.input, fieldStatusClass(field.status))}
      aria-invalid={hasError}
      aria-required={required || undefined}
      aria-describedby={describe(externalDescribedBy)}
      {...forwardedInputProps}
    />,
  );
});

FormFieldEditorial.displayName = "FormFieldEditorial";
